import { toast } from '@affine/component';
import { OrganizeService } from '@affine/core/modules/organize';
import { WorkspaceQuotaService } from '@affine/core/modules/quota';
import { WorkbenchService } from '@affine/core/modules/workbench';
import { DocTransformer } from '@affine/core/utils';
import { ConverterEnum, FeatureName } from '@affine/graphql';
import { useI18n } from '@affine/i18n';
import type { DocCollection } from '@blocksuite/affine/store';
import { DocsService, useLiveData, useServices } from '@toeverything/infra';
import { useMemo } from 'react';

export const useImportDoc = (docCollection: DocCollection) => {
  const t = useI18n();

  const {
    docsService,
    workbenchService,
    organizeService,
    workspaceQuotaService,
  } = useServices({
    DocsService,
    WorkbenchService,
    WorkspaceQuotaService,
    OrganizeService,
  });
  const workbench = workbenchService.workbench;
  const docRecordList = docsService.list;

  const folderTree = organizeService.folderTree;
  const rootFolder = folderTree.rootFolder;

  const quota = useLiveData(workspaceQuotaService.quota.quota$);
  const max = useLiveData(workspaceQuotaService.quota.max$);
  const converters = useLiveData(
    workspaceQuotaService.quota.quota$.selector(q => q?.converters)
  );

  const updatePlan = useMemo(() => {
    if (quota?.name === FeatureName.unlimited) {
      return false;
    }

    return !converters?.includes(ConverterEnum.docx_to_md);
  }, [converters, quota]);

  const importFileAndOpen = useMemo(
    () => async () => {
      const { showImportModal } = await import('@blocksuite/affine/blocks');
      const { promise, resolve, reject } =
        Promise.withResolvers<
          Parameters<
            NonNullable<Parameters<typeof showImportModal>[0]['onSuccess']>
          >[1]
        >();
      const onSuccess = (
        pageIds: string[],
        options: { isWorkspaceFile: boolean; importedCount: number }
      ) => {
        resolve(options);

        if (options.isWorkspaceFile || options.importedCount > 0) {
          toast(`Успешно импортировался(-ись) документ(-ы)`);
        }

        if (options.isWorkspaceFile) {
          workbench.openAll();
          return;
        }

        if (pageIds.length === 0) {
          return;
        }
        const pageId = pageIds[0];

        // DNB_FIX не создавать папку, если в результате импорта было создано меньше 2 документов
        const minCountImportedDoc = 2;
        if (options.importedCount < minCountImportedDoc) {
          workbench.openDoc(pageId);
          return;
        }

        // DNB_FIX добавлять папку и документ в "Каталог"
        const title = docRecordList.doc$(pageId).value?.title$.value;
        const folderId = rootFolder.createFolder(
          title || t['com.affine.rootAppSidebar.organize.new-folders'](),
          rootFolder.indexAt('after')
        );

        const folerNode = folderTree.folderNode$(folderId).value;
        folerNode?.createLink('doc', pageId, folerNode.indexAt('after'));

        workbench.openDoc(pageId);
      };
      showImportModal({
        collection: docCollection,
        onSuccess,
        onFail: message => {
          reject(new Error(message));
        },
        docCRUD: {
          create: (id: string) => {
            const used = workspaceQuotaService.quota.used$.value;
            if (
              used.usedDocCount + 1 > max.docLimit &&
              quota?.name !== FeatureName.unlimited
            ) {
              workspaceQuotaService.dialog.open();
              throw new Error('Quota exceeded');
            }
            return docCollection.createDoc({ id });
          },
        },
        updatePlan,
        functionImport: (type, options) => {
          const importDocx = async () => {
            let pages: { pageIds: string[] } = { pageIds: [] };

            if (
              options?.collection &&
              options?.file &&
              options.file instanceof File
            ) {
              pages = await DocTransformer.importDocx(
                options?.collection,
                options?.file
              );
            }
            return { pageIds: pages.pageIds, entryId: '' };
          };
          if (type === 'docx' && !updatePlan) {
            const used = workspaceQuotaService.quota.used$.value;
            if (
              used.usedDocCount + 1 > max.docLimit &&
              quota?.name !== FeatureName.unlimited
            ) {
              workspaceQuotaService.dialog.open();
              throw new Error('Quota exceeded');
            }

            return importDocx;
          } else {
            return null;
          }
        },
      });
      return await promise;
    },
    [
      docCollection,
      updatePlan,
      docRecordList,
      rootFolder,
      t,
      folderTree,
      workbench,
      workspaceQuotaService.quota.used$.value,
      workspaceQuotaService.dialog,
      max,
      quota?.name,
    ]
  );

  return useMemo(() => {
    return {
      importFile: importFileAndOpen,
    };
  }, [importFileAndOpen]);
};
